import React from "react";
import { Link } from "react-router-dom";
import Helmet from "react-helmet";
import chatCultureIcon from "../assets/app-icons/chatculture-icon.png";
import translateAppIcon from "../assets/app-icons/translate-text-voice-icon.png";
import {
  brandIconPath,
  buildWebPageSchema,
  chatCultureAppStoreUrl,
  chatCultureIconUrl,
  chatCultureUrl,
  heroImagePath,
  organizationSchema,
  pageUrls,
  siteName,
  siteUrl,
  socialImageAlt,
  socialImageUrl,
  themeColor,
  translateAppIconUrl,
  translateAppStoreUrl,
  websiteSchema,
} from "../seo";
import "../App.css";

const pageTitle = "OmniMinds | AI Consulting, Scientific Software & App Development";
const pageDescription =
  "OmniMinds builds AI-native apps and scientific software, from technical strategy and prototyping to polished mobile products like ChatCulture and Translate Text & Voice.";
const shortPageDescription =
  "AI-native product engineering, scientific software consulting, and app development.";
const heroImageAlt =
  "Modern workspace visual representing OmniMinds AI consulting, scientific software, and product engineering.";

const capabilities = [
  {
    title: "AI product strategy",
    description:
      "Shape technical ideas into sharp product plans, prototypes, and launch paths.",
  },
  {
    title: "Scientific software consulting",
    description:
      "Connect research, data, automation, and engineering into usable systems.",
  },
  {
    title: "Mobile app development",
    description:
      "Design and ship polished apps with modern UX and scalable architecture.",
  },
];

const faqs = [
  {
    question: "What is OmniMinds?",
    answer:
      "OmniMinds is a scientific consulting and app-building company focused on AI-native products, technical strategy, and real software execution.",
  },
  {
    question: "What is ChatCulture?",
    answer:
      "ChatCulture is OmniMinds' advanced chat translation app, built for translated messaging, language learning, AI chat, camera, document, voice, photo, object, and AR translation.",
  },
  {
    question: "What is Translate Text & Voice?",
    answer:
      "Translate Text & Voice is a simpler second app for fast text translation, voice translation, pronunciation, phrasebook access, favorites, and 100+ languages.",
  },
];

function Home() {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  const webPageSchema = buildWebPageSchema({
    url: pageUrls.home,
    name: pageTitle,
    description: pageDescription,
    image: socialImageUrl,
    imageAlt: socialImageAlt,
  });

  const appListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "@id": `${siteUrl}/#published-work`,
    name: "OmniMinds published work",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        url: chatCultureUrl,
        item: {
          "@type": "SoftwareApplication",
          "@id": `${chatCultureUrl}/#software`,
          name: "ChatCulture",
          alternateName: "ChatCulture: Chat & Translate",
          applicationCategory: "CommunicationApplication",
          operatingSystem: "iOS",
          url: chatCultureUrl,
          image: chatCultureIconUrl,
          publisher: {
            "@id": `${siteUrl}/#organization`,
          },
          downloadUrl: chatCultureAppStoreUrl,
          description:
            "ChatCulture is an OmniMinds app for translated messaging, language learning, AI chat, and multimodal translation tools.",
        },
      },
      {
        "@type": "ListItem",
        position: 2,
        url: pageUrls.translateApp,
        item: {
          "@type": "SoftwareApplication",
          "@id": `${pageUrls.translateApp}#software`,
          name: "Translate Text & Voice",
          applicationCategory: "ProductivityApplication",
          operatingSystem: "iOS, Android",
          url: pageUrls.translateApp,
          image: translateAppIconUrl,
          publisher: {
            "@id": `${siteUrl}/#organization`,
          },
          downloadUrl: translateAppStoreUrl,
          description:
            "Translate Text & Voice is an OmniMinds app for text translation, voice translation, pronunciation, saved phrases, favorites, dialects, and 100+ languages.",
        },
      },
    ],
  };

  return (
    <div className="site-shell">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="application-name" content={siteName} />
        <meta name="apple-mobile-web-app-title" content={siteName} />
        <meta name="theme-color" content={themeColor} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrls.home} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={shortPageDescription} />
        <meta property="og:url" content={pageUrls.home} />
        <meta property="og:image" content={socialImageUrl} />
        <meta property="og:image:alt" content={socialImageAlt} />
        <meta property="og:image:width" content="1672" />
        <meta property="og:image:height" content="941" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={shortPageDescription} />
        <meta name="twitter:image" content={socialImageUrl} />
        <meta name="twitter:image:alt" content={socialImageAlt} />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(appListSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <header className="site-header">
        <Link to="/" className="brand-link" aria-label="OmniMinds home">
          <img className="brand-mark" src={brandIconPath} alt="" width="32" height="32" />
          <span>OmniMinds</span>
        </Link>
        <nav className="site-nav" aria-label="Main navigation">
          <a href="#work">Products</a>
          <a href="#capabilities">Capabilities</a>
          <Link to="/translateapp">Translate App</Link>
          <a href="https://chatculture.app" target="_blank" rel="noopener noreferrer">
            ChatCulture
          </a>
        </nav>
      </header>

      <main>
        <section className="home-hero">
          <div className="hero-copy">
            <p className="eyebrow">AI consulting and product engineering</p>
            <h1>Ideas built into useful software.</h1>
            <p className="hero-lede">
              OmniMinds turns technical strategy, applied AI, and focused engineering into
              software people can actually use.
            </p>
            <div className="hero-actions">
              <a
                href="https://chatculture.app"
                className="primary-action"
                target="_blank"
                rel="noopener noreferrer"
              >
                Explore ChatCulture
              </a>
              <Link to="/translateapp" className="secondary-action">
                View Translate Text & Voice
              </Link>
            </div>
            <dl className="hero-metrics" aria-label="OmniMinds highlights">
              <div>
                <dt>AI</dt>
                <dd>strategy</dd>
              </div>
              <div>
                <dt>Apps</dt>
                <dd>shipped</dd>
              </div>
              <div>
                <dt>R&D</dt>
                <dd>consulting</dd>
              </div>
            </dl>
          </div>
          <div className="hero-visual">
            <img
              src={heroImagePath}
              alt={heroImageAlt}
              width="1672"
              height="941"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </div>
        </section>

        <section className="products-section" id="work">
          <div className="section-heading">
            <p className="eyebrow">Published work</p>
            <h2>Apps shaped around real use cases.</h2>
          </div>
          <div className="product-grid">
            <article className="product-panel product-panel-featured">
              <div className="product-logo-row">
                <img
                  src={chatCultureIcon}
                  alt="ChatCulture app icon"
                  width="48"
                  height="48"
                  loading="lazy"
                  decoding="async"
                />
                <p className="product-kicker">Chat translation and language discovery</p>
              </div>
              <h3>ChatCulture</h3>
              <p>
                ChatCulture helps people message, learn, and explore languages with translated
                chat, AI Pal, global AI characters, and multimodal translation tools.
              </p>
              <div className="product-actions">
                <a
                  href="https://chatculture.app"
                  className="inline-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Visit website
                </a>
                <a
                  href={chatCultureAppStoreUrl}
                  className="inline-link inline-link-muted"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  App Store
                </a>
              </div>
            </article>
            <article className="product-panel">
              <div className="product-logo-row">
                <img
                  src={translateAppIcon}
                  alt="Translate Text & Voice app icon"
                  width="48"
                  height="48"
                  loading="lazy"
                  decoding="async"
                />
                <p className="product-kicker">Text, voice, and phrasebook translation</p>
              </div>
              <h3>Translate Text & Voice</h3>
              <p>
                Translate Text & Voice is a fast utility for text translation, speech,
                pronunciation, saved phrases, favorites, dialects, and 100+ languages.
              </p>
              <div className="product-actions">
                <Link to="/translateapp" className="inline-link">
                  View app page
                </Link>
                <a
                  href={translateAppStoreUrl}
                  className="inline-link inline-link-muted"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  App Store
                </a>
              </div>
            </article>
          </div>
        </section>

        <section className="section-band" id="capabilities">
          <div className="section-heading">
            <p className="eyebrow">What OmniMinds does</p>
            <h2>Strategy, software, and execution.</h2>
          </div>
          <div className="feature-grid">
            {capabilities.map((capability) => (
              <article className="feature-card" key={capability.title}>
                <div className="feature-icon" aria-hidden="true">
                  <span />
                </div>
                <h3>{capability.title}</h3>
                <p>{capability.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="faq-section">
          <div className="section-heading">
            <p className="eyebrow">FAQ</p>
            <h2>Quick answers.</h2>
          </div>
          <div className="faq-list">
            {faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div>
          <Link to="/" className="footer-brand">
            OmniMinds
          </Link>
          <p>Scientific consulting, AI product engineering, and app development.</p>
        </div>
        <nav aria-label="Footer navigation">
          <a href="https://chatculture.app" target="_blank" rel="noopener noreferrer">
            ChatCulture
          </a>
          <Link to="/translateapp">Translate App</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:contact@omniminds.org">Contact</a>
        </nav>
        <p className="copyright">Copyright {new Date().getFullYear()} OmniMinds. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default Home;
